import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { marked } from 'marked';

// Set up fonts - handle different import formats
try {
    if (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
    } else if (pdfFonts && pdfFonts.default && pdfFonts.default.pdfMake) {
        pdfMake.vfs = pdfFonts.default.pdfMake.vfs;
    } else if (pdfFonts && typeof pdfFonts === 'object') {
        pdfMake.vfs = pdfFonts;
    } else {
        console.warn('Could not load pdfMake fonts, using default fonts');
    }
} catch (error) {
    console.warn('Error setting up pdfMake fonts:', error);
}

export class PDFGenerator {
    constructor() {
        this.imageCache = new Map(); // Cache downloaded images
        this.styles = {
            heading1: {
                fontSize: 26,
                bold: false,
                margin: [0, 0, 0, 10],
                font: 'NotoSerif'
            },
            heading2: {
                fontSize: 20,
                bold: false,
                margin: [0, 10, 0, 5],
                font: 'NotoSerif'
            },
            heading3: {
                fontSize: 18,
                bold: false,
                margin: [0, 10, 0, 5],
                font: 'NotoSerif'
            },
            heading4: {
                fontSize: 16,
                bold: false,
                margin: [0, 10, 0, 5],
                font: 'NotoSerif'
            },
            heading5: {
                fontSize: 14,
                bold: false,
                margin: [0, 10, 0, 5],
                font: 'NotoSerif'
            },
            heading6: {
                fontSize: 12,
                bold: true,
                margin: [0, 10, 0, 5],
                font: 'NotoSerif'
            },
            footer: {
                fontSize: 12,
                bold: true,
                margin: [0, 10, 0, 5],
                font: 'NotoSerif'
            },
            tableheader: {
                fontSize: 11,
                bold: true,
                margin: [5, 5, 5, 5],
                lineHeight: 1.2,
            },
            tablecell: {
                fontSize: 11,
                margin: [5, 5, 5, 5],
                lineHeight: 1.2,
            },
            paragraph: {
                fontSize: 11,
                margin: [0, 0, 0, 10],
                alignment: 'justify',
                lineHeight: 1.5,
            },
            code: {
                fontSize: 11,
                font: 'Courier',
                background: '#f5f5f5',
                margin: [10, 10, 10, 10],
                fillColor: '#f5f5f5'
            },
            codeBlock: {
                fontSize: 11,
                font: 'Courier',
                margin: [5, 5, 5, 5],
                fillColor: '#f5f5f5',
                preserveLeadingSpaces: true,
                lineHeight: 1.2,
            },
            quote: {
                fontSize: 11,
                italics: true,
                margin: [20, 5, 0, 5],
                color: '#666666',
                lineHeight: 1.2
            },
            listItem: {
                fontSize: 11,
                margin: [0, 2, 0, 2],
                lineHeight: 1.5
            },
            taskItem: {
                fontSize: 11,
                margin: [0, 3, 0, 3],
                lineHeight: 1.5,
                marginLeft: 0
            },
            listMarker: {
                color: '#a8a8a8'
            },
            checkbox: {
                fontSize: 11,
                color: '#666666',
                font: 'Courier'
            }
        };
    }





    /**
     * Convert a marked token to a pdfmake element
     */
    convertTokenToPdfElement(token) {
        switch (token.type) {
            case 'heading':
                return {
                    text: this.parseInlineTokens(token.tokens),
                    style: `heading${token.depth}`,
                    margin: token.depth === 1 ? [0, 20, 0, 10] : [0, 15, 0, 8]
                };

            case 'paragraph':
                const paragraphContent = this.parseInlineTokens(token.tokens);

                // If paragraph contains only an image, return the image directly
                if (paragraphContent && typeof paragraphContent === 'object' && paragraphContent.image) {
                    return paragraphContent;
                }

                // If paragraph contains an array with only one image, return the image directly
                if (Array.isArray(paragraphContent) && paragraphContent.length === 1 &&
                    typeof paragraphContent[0] === 'object' && paragraphContent[0].image) {
                    return paragraphContent[0];
                }

                // Otherwise, return as normal paragraph
                return {
                    text: paragraphContent,
                    style: 'paragraph'
                };

            case 'list':
                return this.convertListToken(token);

            case 'code':
                return {
                    table: {
                        widths: [500],
                        body: [
                            [
                                {
                                    text: token.text,
                                    style: 'codeBlock'
                                }
                            ]
                        ]
                    },
                    layout: 'noBorders',
                    margin: [0, 10, 0, 10]
                };

            case 'blockquote':
                return {
                    table: {
                        widths: [4, '*'],
                        body: [
                            [
                                {
                                    canvas: [
                                        {
                                            type: 'rect',
                                            x: 0, y: 0,
                                            w: 4, h: 20,
                                            color: '#f5f5f5'
                                        }
                                    ]
                                },
                                {
                                    text: this.parseInlineTokens(token.tokens),
                                    style: 'quote',
                                    margin: [10, 0, 0, 0]
                                }
                            ]
                        ]
                    },
                    layout: 'noBorders',
                    margin: [0, 5, 0, 5],
                    width: '100%'
                };

            case 'table':
                return this.convertTableToken(token);

            case 'hr':
                return {
                    canvas: [
                        {
                            type: 'line',
                            x1: 0, y1: 0,
                            x2: 515, y2: 0,
                            lineWidth: 1,
                            lineColor: '#cccccc'
                        }
                    ],
                    margin: [0, 10, 0, 10]
                };

            case 'image':
                // Handle block-level images
                if (token.href && token.href.startsWith('data:image/')) {
                    // Data URL - can be used directly in pdfmake
                    return {
                        image: token.href,
                        width: 400, // Default width for block images
                        alignment: 'center',
                        margin: [0, 10, 0, 10]
                    };
                } else if (token.href) {
                    // External URL - show placeholder (browser can't fetch directly)
                    return {
                        text: [
                            {
                                text: `[Image: ${token.text || 'Image'}]`,
                                color: '#666666',
                                italics: true
                            },
                            {
                                text: `\nURL: ${token.href}`,
                                fontSize: 9,
                                color: '#888888'
                            }
                        ],
                        style: 'paragraph',
                        margin: [0, 5, 0, 5]
                    };
                } else {
                    // No href - just show alt text
                    return {
                        text: `[Image: ${token.text || 'Image'}]`,
                        color: '#666666',
                        italics: true,
                        style: 'paragraph',
                        margin: [0, 5, 0, 5]
                    };
                }

            case 'space':
                return null; // Skip spaces

            default:
                console.warn('Unhandled token type:', token.type);
                return null;
        }
    }

    /**
     * Convert a list token to pdfmake list structure
     */
    convertListToken(listToken) {
        const listType = listToken.ordered ? 'ol' : 'ul';
        const items = [];

        for (const item of listToken.items) {
            const itemResult = this.parseListItem(item);

            // Add the main text content
            items.push(itemResult.text);

            // Add any nested lists as separate items
            for (const nestedList of itemResult.nestedLists) {
                items.push(nestedList);
            }
        }

        // Check if this is a task list using marked's native properties
        const isTaskList = listToken.items && listToken.items.length > 0 && listToken.items[0].task === true;

        const listObject = {
            [listType]: items,
            style: 'listItem'
        };

        // For task lists, remove the default markers since we're using checkboxes
        if (isTaskList) {
            listObject.type = 'none'; // Remove bullet points/numbers for task lists
        } else {
            listObject.markerColor = '#a8a8a8';
        }

        return listObject;
    }

    /**
     * Parse a list item and handle nested lists
     */
    parseListItem(item) {
        const content = [];
        const nestedLists = [];

        // Check if this is a task list item using marked's native properties
        const isTaskList = item.task === true;
        const isChecked = item.checked === true;

        for (const token of item.tokens) {
            if (token.type === 'list') {
                // Found nested list - collect it separately
                nestedLists.push(this.convertListToken(token));
            } else if (token.type === 'text') {
                // For task lists, marked already removes the [x]/[ ] syntax from the text
                const textContent = this.parseInlineTokens(token.tokens || [{ type: 'text', text: token.text }]);
                if (textContent) {
                    content.push(textContent);
                }
            } else if (token.type === 'paragraph') {
                // Paragraph token - extract the inline content
                const paragraphText = this.parseInlineTokens(token.tokens);
                if (paragraphText) {
                    content.push(paragraphText);
                }
            } else {
                // Other token types
                const tokenContent = this.convertTokenToPdfElement(token);
                if (tokenContent && tokenContent.text) {
                    content.push(tokenContent.text);
                }
            }
        }

        // Build the text content
        let textContent;
        if (content.length === 0) {
            textContent = '';
        } else if (content.length === 1) {
            textContent = content[0];
        } else {
            // Multiple content pieces - flatten them
            textContent = content.flat();
        }

        // If this is a task list item, prepend the checkbox
        if (isTaskList) {
            const checkbox = {
                text: isChecked ? '[X] ' : '[ ] ',
                style: 'checkbox'
            };

            // Ensure we create a proper text array for pdfmake
            let contentArray = [];

            // Add checkbox first
            contentArray.push(checkbox);

            // Add the content
            if (Array.isArray(textContent)) {
                contentArray.push(...textContent);
            } else if (textContent) {
                contentArray.push(textContent);
            }

            // Wrap in {text: []} so pdfmake treats it as a single line
            textContent = {
                text: contentArray,
                style: 'taskItem'
            };
        }

        // For regular list items, also wrap in {text: []} if it's an array for consistency
        if (!isTaskList && Array.isArray(textContent)) {
            textContent = {
                text: textContent
            };
        }

        // Return object that includes both text and nested lists for flattening
        return {
            text: textContent,
            nestedLists: nestedLists
        };
    }

    /**
     * Convert table token to pdfmake table
     */
    convertTableToken(tableToken) {
        const tableBody = [];

        // Add headers
        if (tableToken.header && tableToken.header.length > 0) {
            tableBody.push(tableToken.header.map(cell => ({
                text: this.parseInlineTokens(cell.tokens),
                style: 'tableheader',
                fillColor: '#f0f0f0'
            })));
        }

        // Add rows
        if (tableToken.rows) {
            tableToken.rows.forEach(row => {
                tableBody.push(row.map(cell => ({
                    text: this.parseInlineTokens(cell.tokens),
                    style: 'tablecell'
                })));
            });
        }

        // Calculate column count
        const columnCount = Math.max(
            tableToken.header ? tableToken.header.length : 0,
            ...tableBody.map(row => row.length)
        );

        return {
            table: {
                headerRows: tableToken.header ? 1 : 0,
                widths: Array(columnCount).fill('*'), // Make all columns equal width, full table width
                body: tableBody
            },
            layout: 'lightHorizontalLines',
            width: '100%'
        };
    }

    /**
     * Parse inline tokens (bold, italic, code, links, etc.)
     */
    parseInlineTokens(tokens) {
        if (!tokens || tokens.length === 0) return '';

        const parts = [];

        for (const token of tokens) {
            switch (token.type) {
                case 'text':
                    parts.push(token.text);
                    break;

                case 'strong':
                    parts.push({
                        text: this.parseInlineTokens(token.tokens),
                        bold: true
                    });
                    break;

                case 'em':
                    parts.push({
                        text: this.parseInlineTokens(token.tokens),
                        italics: true
                    });
                    break;

                case 'codespan':
                    parts.push({
                        text: token.text,
                        font: 'Courier',
                        background: '#f5f5f5'
                    });
                    break;

                case 'link':
                    parts.push({
                        text: this.parseInlineTokens(token.tokens),
                        link: token.href,
                        color: 'blue',
                        decoration: 'underline'
                    });
                    break;

                case 'del':
                    parts.push({
                        text: this.parseInlineTokens(token.tokens),
                        decoration: 'lineThrough'
                    });
                    break;

                case 'br':
                    parts.push('\n');
                    break;

                case 'image':
                    // Handle images - check if it's a data URL or external URL
                    if (token.href && token.href.startsWith('data:image/')) {
                        // Data URL - can be used directly in pdfmake
                        parts.push({
                            image: token.href,
                            width: 300, // Default width, can be adjusted
                            alignment: 'center',
                            margin: [0, 5, 0, 5]
                        });
                    } else if (token.href) {
                        // External URL - show placeholder (browser can't fetch directly)
                        parts.push({
                            text: `[Image: ${token.text || 'Image'}]`,
                            color: '#666666',
                            italics: true,
                            margin: [0, 5, 0, 5]
                        });
                        parts.push({
                            text: `\nURL: ${token.href}`,
                            fontSize: 9,
                            color: '#888888',
                            margin: [0, 0, 0, 5]
                        });
                    } else {
                        // No href - just show alt text
                        parts.push({
                            text: `[Image: ${token.text || 'Image'}]`,
                            color: '#666666',
                            italics: true,
                            margin: [0, 5, 0, 5]
                        });
                    }
                    break;

                case 'paragraph':
                    // Handle nested paragraph tokens
                    const paragraphContent = this.parseInlineTokens(token.tokens);
                    if (paragraphContent) {
                        parts.push(paragraphContent);
                    }
                    break;

                default:
                    console.warn('Unhandled inline token type:', token.type);
                    if (token.text) {
                        parts.push(token.text);
                    } else if (token.tokens) {
                        // Try to parse nested tokens
                        const nestedContent = this.parseInlineTokens(token.tokens);
                        if (nestedContent) {
                            parts.push(nestedContent);
                        }
                    }
                    break;
            }
        }

        return parts.length === 1 ? parts[0] : parts;
    }

    /**
     * Download image and convert to base64 data URL (browser version)
     */
    async downloadImageAsBase64(url) {
        console.log(`🔍 downloadImageAsBase64: Starting download for ${url}`);

        // Check cache first
        if (this.imageCache.has(url)) {
            console.log(`💾 Image found in cache: ${url}`);
            return this.imageCache.get(url);
        }

        try {
            console.log(`🌐 Fetching image: ${url}`);
            // Use fetch API for browser compatibility
            const response = await fetch(url);

            if (!response.ok) {
                console.warn(`❌ Failed to download image: ${url} (${response.status})`);
                return null;
            }

            console.log(`📦 Getting array buffer for: ${url}`);
            // Get the image as array buffer
            const arrayBuffer = await response.arrayBuffer();

            console.log(`🔄 Converting to base64, size: ${arrayBuffer.byteLength} bytes`);
            // Convert to base64
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            const base64 = btoa(binary);

            // Get content type from response headers
            const contentType = response.headers.get('content-type') || 'image/jpeg';
            const dataUrl = `data:${contentType};base64,${base64}`;

            console.log(`✅ Successfully converted image to data URL, length: ${dataUrl.length}`);

            // Cache the result
            this.imageCache.set(url, dataUrl);
            return dataUrl;

        } catch (error) {
            console.error(`❌ Error downloading image: ${url}`, error);
            return null;
        }
    }

    /**
     * Process images in tokens and download them
     */
    async processImagesInTokens(tokens) {
        if (!tokens || !Array.isArray(tokens)) {
            console.log('🔍 processImagesInTokens: No tokens or not array', tokens);
            return tokens;
        }

        console.log(`🔍 processImagesInTokens: Processing ${tokens.length} tokens`);
        const processedTokens = [];

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            console.log(`🔍 Token ${i}: type=${token.type}, href=${token.href}, hasTokens=${!!token.tokens}`);

            if (token.type === 'image') {
                console.log(`🖼️ Found image token: href=${token.href}, text=${token.text}`);

                if (token.href && !token.href.startsWith('data:')) {
                    // Download the image
                    console.log(`📥 Downloading nested image: ${token.href}`);
                    const dataUrl = await this.downloadImageAsBase64(token.href);

                    if (dataUrl) {
                        console.log(`✅ Successfully downloaded image, data URL length: ${dataUrl.length}`);
                        // Update the token with the data URL
                        processedTokens.push({
                            ...token,
                            href: dataUrl,
                            originalHref: token.href
                        });
                    } else {
                        console.log(`❌ Failed to download image: ${token.href}`);
                        // Keep original token if download failed
                        processedTokens.push(token);
                    }
                } else {
                    console.log(`🔍 Image already has data URL or no href: ${token.href}`);
                    processedTokens.push(token);
                }
            } else if (token.tokens && Array.isArray(token.tokens)) {
                console.log(`🔄 Recursively processing ${token.tokens.length} nested tokens for ${token.type}`);
                // Recursively process nested tokens
                const nestedTokens = await this.processImagesInTokens(token.tokens);
                processedTokens.push({
                    ...token,
                    tokens: nestedTokens
                });
            } else {
                console.log(`🔍 Token has no nested tokens: ${token.type}`);
                // No nested tokens or not an image
                processedTokens.push(token);
            }
        }

        console.log(`🔍 processImagesInTokens: Returning ${processedTokens.length} processed tokens`);
        return processedTokens;
    }

    /**
     * Parse markdown content into pdfmake content array using marked lexer (async version)
     */
    async parseMarkdownAsync(markdown) {
        const tokens = marked.lexer(markdown);
        console.log('🚀 parseMarkdownAsync: Parsed', tokens.length, 'top-level tokens');

        // Process images in all tokens
        const processedTokens = [];
        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            console.log(`🔍 Top-level token ${i}: type=${token.type}, hasTokens=${!!token.tokens}`);

            if (token.type === 'image' && token.href && !token.href.startsWith('data:')) {
                // Download top-level image
                console.log(`📥 Downloading top-level image: ${token.href}`);
                const dataUrl = await this.downloadImageAsBase64(token.href);

                if (dataUrl) {
                    console.log(`✅ Successfully downloaded top-level image`);
                    processedTokens.push({
                        ...token,
                        href: dataUrl,
                        originalHref: token.href
                    });
                } else {
                    console.log(`❌ Failed to download top-level image`);
                    processedTokens.push(token);
                }
            } else if (token.tokens) {
                console.log(`🔄 Processing nested tokens for ${token.type} (${token.tokens.length} nested tokens)`);
                const processedNestedTokens = await this.processImagesInTokens(token.tokens);
                processedTokens.push({
                    ...token,
                    tokens: processedNestedTokens
                });
            } else {
                console.log(`🔍 Token ${token.type} has no nested tokens`);
                processedTokens.push(token);
            }
        }

        console.log('🔄 Converting processed tokens to PDF elements...');
        const content = [];

        for (const token of processedTokens) {
            const pdfElement = this.convertTokenToPdfElement(token);
            if (pdfElement) {
                content.push(pdfElement);
            }
        }

        console.log(`✅ parseMarkdownAsync: Generated ${content.length} PDF elements`);
        return content;
    }

    /**
     * Convert markdown content to PDF document definition (async version)
     */
    async markdownToPdfDefinitionAsync(markdown, title = 'Document') {
        const content = await this.parseMarkdownAsync(markdown);
        // here

        // Extract title from first heading if available
        const firstHeading = content.find(element =>
            element && element.style && element.style.startsWith('heading')
        );

        if (firstHeading && firstHeading.text) {
            // Extract plain text from heading (handle both string and array formats)
            if (typeof firstHeading.text === 'string') {
                title = firstHeading.text;
            } else if (Array.isArray(firstHeading.text)) {
                // Flatten array and extract text content
                title = firstHeading.text.map(item =>
                    typeof item === 'string' ? item : (item.text || '')
                ).join('').trim();
            }
        }

        return {
            content: content,
            footer: function (currentPage, pageCount) {
                return {
                    text: `${title} | ${currentPage.toString()} of ${pageCount}`,
                    style: 'footer',
                    alignment: 'center'
                };
            },
            styles: this.styles,
            defaultStyle: {
                fontSize: 11,
                font: 'Roboto'
            },
            pageMargins: [40, 60, 40, 60],
            fonts: {
                Roboto: {
                    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
                    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
                    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
                    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
                },
                Courier: {
                    normal: 'https://fonts.cdnfonts.com/s/16424/CourierPrime-Regular.woff',
                    bold: 'https://fonts.cdnfonts.com/s/16424/CourierPrime-Regular.woff',
                    italics: 'https://fonts.cdnfonts.com/s/16424/CourierPrime-Regular.woff',
                    bolditalics: 'https://fonts.cdnfonts.com/s/16424/CourierPrime-Regular.woff'
                },
                NotoSerif: {
                    normal: 'https://fonts.cdnfonts.com/s/12250/NotoSerif-Regular.woff',
                    bold: 'https://fonts.cdnfonts.com/s/12250/NotoSerif-Bold.woff',
                    italics: 'https://fonts.cdnfonts.com/s/12250/NotoSerif-Italic.woff',
                    bolditalics: 'https://fonts.cdnfonts.com/s/12250/NotoSerif-BoldItalic.woff'
                }
            }
        };
    }


    /**
     * Generate PDF with async image processing and return as blob
     */
    async generatePDFBlobAsync(markdown, title = 'Document') {
        const docDefinition = await this.markdownToPdfDefinitionAsync(markdown, title);

        return new Promise((resolve, reject) => {
            pdfMake.createPdf(docDefinition).getBlob((blob) => {
                resolve(blob);
            });
        });
    }

    /**
     * Generate and download PDF with async image processing
     */
    async generatePDFAsync(markdown, filename = 'document.pdf', title = 'Document') {
        const docDefinition = await this.markdownToPdfDefinitionAsync(markdown, title);

        return new Promise((resolve, reject) => {
            try {
                const pdfDoc = pdfMake.createPdf(docDefinition, null, docDefinition.fonts);

                // Use getBlob to wait for PDF generation, then trigger download
                pdfDoc.getBlob((blob) => {
                    // Create download link and trigger download
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);

                    resolve();
                });
            } catch (error) {
                reject(error);
            }
        });
    }
}